import Phaser from 'phaser';
import { SCENE_KEYS, GAME_CONFIG, VERSION } from '@/config/constants';
import { transitionTo, fadeIn } from '@/utils/UIAnimations';
import { createPillButton } from '@/ui/PillButton';
import { drawSelectionHalo } from '@/ui/SelectionHalo';
import { COLORS, COLORS_HEX, TEXT_PRESET } from '@/ui/theme';
import { GamepadNav } from '@/utils/GamepadNav';

/**
 * Credits Scene — crystal-energy credit lane with premium finish
 */
export class CreditsScene extends Phaser.Scene {
  private nav?: GamepadNav;

  constructor() {
    super({ key: SCENE_KEYS.CREDITS });
  }

  update(): void {
    this.nav?.update();
  }

  create(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;
    const cy = HEIGHT / 2;
    fadeIn(this, 400);

    const videoKey = 'bg_video_world1_video';
    let videoBg: Phaser.GameObjects.Video | undefined;
    if (this.cache.video.has(videoKey)) {
      videoBg = this.add.video(cx, cy, videoKey);
      videoBg.setDepth(0).setScale(1).setVolume(0).play(true).setAlpha(0.56);
    }

    const bg = this.add.graphics().setDepth(1);
    bg.fillGradientStyle(COLORS.midnightDepth, COLORS.midnightDepth, COLORS.deepOcean, COLORS.deepOcean, 0.9, 0.9, 0.98, 0.98);
    bg.fillRect(0, 0, WIDTH, HEIGHT);

    const glows = this.add.graphics().setDepth(2);
    glows.fillStyle(COLORS.aqua, 0.08);
    glows.fillCircle(cx, cy - 220, 280);
    glows.fillStyle(COLORS.sapphire, 0.12);
    glows.fillCircle(cx - 420, cy + 80, 240);
    glows.fillCircle(cx + 420, cy + 120, 240);

    // Frame sits BELOW the title block (OCEAN CREW / POSEIDON'S CHALLENGE /
    // version) and runs to near the bottom edge. The scrolling names are masked
    // to this frame's interior, so they travel within it rather than over the
    // title or up from the bottom of the screen.
    const frameTop = 172;
    const frameBottom = HEIGHT - 80;
    const frameH = frameBottom - frameTop;

    const lane = this.add.graphics().setDepth(3);
    lane.fillStyle(COLORS.aqua, 0.06);
    lane.fillRoundedRect(cx - 360, frameTop, 720, frameH, 42);
    lane.fillStyle(COLORS.midnightDepth, 0.46);
    lane.fillRoundedRect(cx - 340, frameTop + 24, 680, frameH - 48, 34);
    lane.lineStyle(2, COLORS.aqua, 0.56);
    lane.strokeRoundedRect(cx - 340, frameTop + 24, 680, frameH - 48, 34);
    lane.lineStyle(1, COLORS.pearlWhite, 0.16);
    lane.strokeRoundedRect(cx - 334, frameTop + 30, 668, frameH - 60, 28);

    const vignette = this.add.graphics().setDepth(4);
    vignette.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.82, 0.82, 0, 0).fillRect(0, 0, WIDTH, 150);
    vignette.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0, 0.84, 0.84).fillRect(0, HEIGHT - 150, WIDTH, 150);

    const titleLabel = this.add.text(cx, 66, 'OCEAN CREW', {
      ...TEXT_PRESET.label,
      fontSize: '18px',
      color: COLORS_HEX.aquaLight,
      letterSpacing: 6,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(5);

    const titleMain = this.add.text(cx, 104, 'POSEIDON\'S CHALLENGE', {
      ...TEXT_PRESET.section,
      fontSize: '52px',
      color: COLORS_HEX.goldLight,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(5);

    const titleVersion = this.add.text(cx, 140, VERSION, {
      ...TEXT_PRESET.caption,
      fontSize: '20px',
      color: COLORS_HEX.textTertiary,
      letterSpacing: 2,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(5);

    const credits = [
      { text: 'A GAME DEVELOPED FOR', size: '22px', color: COLORS_HEX.aquaLight, gap: 18, preset: 'label' },
      { text: 'HELMEPA', size: '48px', color: COLORS_HEX.textPrimary, gap: 48, preset: 'section' },

      { text: 'DEVELOPED BY', size: '22px', color: COLORS_HEX.aquaLight, gap: 18, preset: 'label' },
      { text: 'Designco', size: '40px', color: COLORS_HEX.textPrimary, gap: 48, preset: 'section' },
      { text: 'DESIGNED BY', size: '22px', color: COLORS_HEX.aquaLight, gap: 18, preset: 'label' },
      { text: 'Designco & HELMEPA', size: '40px', color: COLORS_HEX.textPrimary, gap: 48, preset: 'section' },

      { text: 'PROJECT LEAD', size: '22px', color: COLORS_HEX.aquaLight, gap: 18, preset: 'label' },
      { text: 'Albert Papadosifakis', size: '36px', color: COLORS_HEX.goldLight, gap: 48, preset: 'section' },

      { text: 'PROJECT MANAGEMENT', size: '22px', color: COLORS_HEX.aquaLight, gap: 18, preset: 'label' },
      { text: 'Costantinos Kavouras', size: '36px', color: COLORS_HEX.textPrimary, gap: 48, preset: 'section' },

      { text: 'HEAD OF DESIGN', size: '22px', color: COLORS_HEX.aquaLight, gap: 18, preset: 'label' },
      { text: 'Constantina Vavilousaki', size: '36px', color: COLORS_HEX.textPrimary, gap: 48, preset: 'section' },

      { text: 'CONCEPT AND PRODUCTION DIRECTOR', size: '22px', color: COLORS_HEX.aquaLight, gap: 18, preset: 'label' },
      { text: 'Olga Stavropoulou', size: '36px', color: COLORS_HEX.textPrimary, gap: 48, preset: 'section' },

      { text: 'GAME ENGINE', size: '22px', color: COLORS_HEX.aquaLight, gap: 18, preset: 'label' },
      { text: 'Michael Kemenes', size: '36px', color: COLORS_HEX.textPrimary, gap: 48, preset: 'section' },

      { text: 'BACKGROUNDS DESIGN', size: '22px', color: COLORS_HEX.aquaLight, gap: 18, preset: 'label' },
      { text: 'Marina Papadopoulou', size: '36px', color: COLORS_HEX.textPrimary, gap: 48, preset: 'section' },

      { text: '3D ARTIST', size: '22px', color: COLORS_HEX.aquaLight, gap: 18, preset: 'label' },
      { text: 'Jonathan Thompson', size: '36px', color: COLORS_HEX.textPrimary, gap: 48, preset: 'section' },

      { text: 'GAME TEXTS', size: '22px', color: COLORS_HEX.aquaLight, gap: 18, preset: 'label' },
      { text: 'Olga Stavropoulou', size: '36px', color: COLORS_HEX.textPrimary, gap: 6, preset: 'section' },
      { text: 'Stathis Kostakopoulos', size: '36px', color: COLORS_HEX.textPrimary, gap: 48, preset: 'section' },

      { text: 'SOUND ENGINEERING', size: '22px', color: COLORS_HEX.aquaLight, gap: 18, preset: 'label' },
      { text: 'Mariangela Ropen', size: '36px', color: COLORS_HEX.textPrimary, gap: 48, preset: 'section' },

      { text: 'ANIMATION & RIGGING', size: '22px', color: COLORS_HEX.aquaLight, gap: 18, preset: 'label' },
      { text: 'Thalia Liossi', size: '36px', color: COLORS_HEX.textPrimary, gap: 48, preset: 'section' },

      { text: 'STAGE DESIGN', size: '22px', color: COLORS_HEX.aquaLight, gap: 18, preset: 'label' },
      { text: 'Mack "Xeonix"', size: '36px', color: COLORS_HEX.textPrimary, gap: 48, preset: 'section' },

      { text: 'PROTECT OUR OCEANS', size: '48px', color: COLORS_HEX.goldLight, gap: 28, preset: 'section' },
      { text: 'Every fresh run is another wave against the tide.', size: '28px', color: COLORS_HEX.textSecondary, gap: 0, preset: 'body' },
    ];

    // Frame interior band — the scrolling names live ONLY inside this rect.
    // They are clipped to it by a dedicated camera set up at the end of create()
    // (Phaser 4's renderer does not reliably geometry-mask Text objects).
    const interiorX = cx - 340;
    const interiorTop = frameTop + 24;
    const interiorBottom = frameBottom - 24;
    const interiorW = 680;
    const interiorH = interiorBottom - interiorTop;

    let y = interiorBottom + 60; // first line starts just below the frame's bottom edge
    const startY = y;
    const elements: Phaser.GameObjects.Text[] = [];

    for (const line of credits) {
      const baseStyle =
        line.preset === 'label'
          ? TEXT_PRESET.label
          : line.preset === 'body'
            ? TEXT_PRESET.bodyBig
            : TEXT_PRESET.section;

      const t = this.add.text(cx, y, line.text, {
        ...baseStyle,
        fontSize: line.size,
        color: line.color,
        align: 'center',
      } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(5);
      elements.push(t);
      y += parseInt(line.size, 10) + (line.gap || 20);
    }

    const contentHeight = y - startY;
    // Travel far enough that the last line clears the top of the frame.
    const scrollDistance = contentHeight + interiorH + 120;
    const scrollDuration = scrollDistance * 16;
    this.tweens.add({
      targets: elements,
      y: `-=${scrollDistance}`,
      duration: scrollDuration,
      ease: 'Linear',
      onComplete: () => transitionTo(this, SCENE_KEYS.MENU),
    });

    const skipBtn = createPillButton(this, {
      x: WIDTH - 112, y: HEIGHT - 54,
      label: 'SKIP',
      variant: 'secondary',
      size: 'small',
      width: 150, height: 50,
      onClick: () => transitionTo(this, SCENE_KEYS.MENU),
    });
    skipBtn.setDepth(6);

    // SKIP is the only control on this card, so keep it permanently focused:
    // ENTER / SPACE / ESC and any gamepad button press it. Pressing the pill
    // (rather than calling transitionTo directly) gives the click feedback and
    // matches the keydown-* wiring used on the other screens.
    const skip = () => { (skipBtn as any).press?.(); };

    const kb = this.input.keyboard;
    if (kb) {
      kb.on('keydown-ENTER', skip);
      kb.on('keydown-SPACE', skip);
      kb.on('keydown-ESC', skip);
    }

    // Gamepad: any face button or Start → back to menu (skip).
    this.nav = new GamepadNav(this, { onConfirm: skip, onCancel: skip, onPause: skip });

    // Selection halo always on the lone SKIP CTA so keyboard and joystick
    // players can see it is the active control.
    const skipHalo = this.add.graphics().setDepth(7);
    this.events.on(Phaser.Scenes.Events.UPDATE, () => {
      if (!skipBtn.active) { skipHalo.clear(); return; }
      drawSelectionHalo(skipHalo, {
        cx: skipBtn.x,
        cy: skipBtn.y,
        w: ((skipBtn as any).pillWidth ?? 150) * skipBtn.scaleX,
        h: ((skipBtn as any).pillHeight ?? 50) * skipBtn.scaleY,
        rx: ((skipBtn as any).pillCapRx ?? 25) * skipBtn.scaleX,
        ry: ((skipBtn as any).pillCapRy ?? 25) * skipBtn.scaleY,
      }, { gap: 10 });
    });

    // ── Clip the scrolling names to the frame interior with a dedicated camera.
    // Phaser 4's renderer does not reliably geometry-mask Text, so instead a
    // second camera whose viewport == the frame interior renders ONLY the credit
    // lines (hard-scissored to that rect). Its scroll is offset so world coords
    // land at their natural on-screen positions. The main camera renders
    // everything else and ignores the lines. Result: names appear only inside
    // the frame, rising from the bottom edge and clipping out at the top edge.
    const creditsCam = this.cameras.add(interiorX, interiorTop, interiorW, interiorH);
    creditsCam.setScroll(interiorX, interiorTop);
    const nonCredits: Phaser.GameObjects.GameObject[] = [
      bg, glows, lane, vignette, titleLabel, titleMain, titleVersion, skipBtn, skipHalo,
    ];
    if (videoBg) nonCredits.push(videoBg);
    creditsCam.ignore(nonCredits);
    this.cameras.main.ignore(elements);
  }
}
